import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';

import { colour } from './tokens';

/**
 * Take this list away — on paper, or as a file.
 *
 * The AR team work down these lists ringing families, and what they asked for was to be able to do
 * that away from the screen. Printing from the browser would carry the whole page: the navigation,
 * the charts, the filters. So a print opens just the rows, already laid out for paper.
 *
 * The export is a real CSV built here rather than the grid's own, because the grid only holds the
 * page it is showing and half a list is worse than none.
 */

const csvCell = (value) => {
    const text = value == null ? '' : String(value);
    // A family name with a comma, or an address with one, would otherwise become two columns.
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const toCsv = (columns, rows) => [
    columns.map((column) => csvCell(column.header)).join(','),
    ...rows.map((row) => columns.map((column) => csvCell(column.value(row))).join(',')),
].join('\r\n');

/** Hand a file to the browser. Exported so the chart and page actions can reuse it. */
export const download = (name, body) => {
    const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

/**
 * Print a document we have written ourselves, without opening a window.
 *
 * This used to be `window.open('', '_blank')`. Two ways that failed. A pop-up blocker turned the
 * whole feature into a button that did nothing — `window.open` returns null and the old code
 * returned silently, so there was not even an error to look at. And "Print all" has to rasterise
 * its charts first, which means an `await` before the window is asked for; Safari treats the click
 * as spent by then and refuses, so the one print that most needed to work was the one that never
 * could.
 *
 * A hidden iframe needs no permission and does not care how long we took to build the page.
 *
 * The images are waited for before the dialog opens. A chart is a data: URL, which decodes fast
 * enough to look instant and not fast enough to be there already — printing without the wait gave
 * an empty box where the chart should be.
 */
const printDocument = (html) => {
    const frame = document.createElement('iframe');

    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('title', 'Print');
    // Off-screen rather than display:none — a hidden frame has no layout, and a chart with no
    // layout prints at zero height in Firefox.
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:1px;height:1px;opacity:0;border:0;';
    document.body.appendChild(frame);

    const page = frame.contentDocument;
    if (!page) {
        frame.remove();
        return;
    }

    page.open();
    page.write(html);
    page.close();

    const print = () => {
        frame.contentWindow.focus();
        frame.contentWindow.print();
        // Removing the frame while the dialog is still up cancels the print in Safari, so it is
        // left behind until well after the dialog has had its answer.
        setTimeout(() => frame.remove(), 60000);
    };

    const pictures = Array.from(page.images || []);
    const pending = pictures.filter((picture) => !picture.complete);
    if (!pending.length) {
        print();
        return;
    }

    let waitingFor = pending.length;
    // A chart that will not decode must not hold the paperwork hostage: onerror counts too.
    const ready = () => { if (--waitingFor === 0) print(); };
    pending.forEach((picture) => {
        picture.addEventListener('load', ready, { once: true });
        picture.addEventListener('error', ready, { once: true });
    });
};

/**
 * Prints just the rows, styled for paper.
 *
 * `imageDataUrl` is for a chart section: the picture goes above its own figures, because a chart
 * printed without them cannot be checked and the figures printed without it lose the shape that
 * made somebody ask.
 */
export const printRows = ({ title, columns, rows, note = '', imageDataUrl = '' }) => {
    const head = columns.map((column) => `<th>${column.header}</th>`).join('');
    const body = rows.map((row) => (
        `<tr>${columns.map((column) => `<td>${String(column.value(row) ?? '')}</td>`).join('')}</tr>`
    )).join('');
    const picture = imageDataUrl
        ? `<img class="chart" src="${imageDataUrl}" alt="${title}" />`
        : '';

    printDocument(`<!doctype html><html><head><title>${title}</title><style>
        body { font-family: system-ui, sans-serif; margin: 24px; color: #0F172A; }
        h1 { font-size: 18px; margin: 0 0 4px; }
        p.note { font-size: 12px; color: #64748B; margin: 0 0 16px; }
        table { border-collapse: collapse; width: 100%; font-size: 12px; }
        th, td { border-bottom: 1px solid #E2E8F0; text-align: left; padding: 6px 8px; }
        th { background: #F8FAFC; }
        /* A long list runs over pages, and a header on only the first one makes the rest unreadable. */
        thead { display: table-header-group; }
        tr { page-break-inside: avoid; }
        img.chart { max-width: 100%; height: auto; margin: 0 0 16px; }
    </style></head><body>
        <h1>${title}</h1>
        <p class="note">${note}${note ? ' · ' : ''}${rows.length} rows · printed ${new Date().toLocaleString()}</p>
        ${picture}
        <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
    </body></html>`);
};

/**
 * One paper document holding several sections, in the order they appear on screen.
 *
 * The whole-page print. Each section carries its own heading, optionally its chart, and its own
 * table — the sections have different columns, so flattening them into one table would produce
 * something nobody could read. Sections with no rows are dropped rather than printed empty: a
 * heading over nothing reads as a fault in the report.
 */
export const printSections = ({ title, note = '', sections = [] }) => {
    const shown = sections.filter((section) => section?.rows?.length || section?.imageDataUrl);
    if (!shown.length) return;

    const body = shown.map((section) => {
        const head = (section.columns || []).map((column) => `<th>${column.header}</th>`).join('');
        const rows = (section.rows || []).map((row) => (
            `<tr>${(section.columns || []).map((column) => `<td>${String(column.value(row) ?? '')}</td>`).join('')}</tr>`
        )).join('');

        return `<section>
            <h2>${section.heading}</h2>
            ${section.note ? `<p class="note">${section.note}</p>` : ''}
            ${section.imageDataUrl ? `<img class="chart" src="${section.imageDataUrl}" alt="${section.heading}" />` : ''}
            ${rows ? `<table><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>` : ''}
        </section>`;
    }).join('');

    printDocument(`<!doctype html><html><head><title>${title}</title><style>
        body { font-family: system-ui, sans-serif; margin: 24px; color: #0F172A; }
        h1 { font-size: 20px; margin: 0 0 4px; }
        h2 { font-size: 15px; margin: 0 0 6px; }
        p.note { font-size: 12px; color: #64748B; margin: 0 0 12px; }
        table { border-collapse: collapse; width: 100%; font-size: 12px; }
        th, td { border-bottom: 1px solid #E2E8F0; text-align: left; padding: 6px 8px; }
        th { background: #F8FAFC; }
        thead { display: table-header-group; }
        tr { page-break-inside: avoid; }
        img.chart { max-width: 100%; height: auto; margin: 0 0 12px; }
        /* Each section starts on its own page: a chart split across a fold is worth nothing. */
        section { margin: 0 0 28px; page-break-inside: avoid; }
    </style></head><body>
        <h1>${title}</h1>
        <p class="note">${note}${note ? ' \u00b7 ' : ''}printed ${new Date().toLocaleString()}</p>
        ${body}
    </body></html>`);
};

const ListActions = ({ title, columns, rows, note = '', fileName }) => {
    if (!rows.length) return null;

    const stamp = new Date().toISOString().slice(0, 10);
    const buttonSx = { textTransform: 'none', fontWeight: 700, fontSize: '13px', color: colour.primaryDeep };

    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Button
                size="small"
                startIcon={<PrintIcon sx={{ fontSize: 17 }} />}
                onClick={() => printRows({ title, columns, rows, note })}
                sx={buttonSx}
            >
                Print
            </Button>
            <Button
                size="small"
                startIcon={<DownloadIcon sx={{ fontSize: 17 }} />}
                onClick={() => download(`${fileName || 'list'}-${stamp}.csv`, toCsv(columns, rows))}
                sx={buttonSx}
            >
                Export
            </Button>
        </Box>
    );
};

export default ListActions;
